import {
  emptyObject,
  emptyArray,
  constant,
  concatTo,
} from '@packages/utilities/fp/utils';
import {weakMemo, singleMemo} from '@packages/utilities/memo';
import {
  isUserComponentRootId,
  isUserComponentInst,
  reduceElementTree,
  getDescendants,
} from '@packages/utilities/core-elements';

import {
  isFormForm,
  isFormCheckboxInput,
  isFormRadioInput,
  isFormTextarea,
  isFormFileUpload,
  isFormBasicInputElement,
  isFormSelect,
  isFormReCaptcha,
} from '@packages/utilities/elementTypeUtils';
import type {
  Forms,
  Form,
  ComponentId,
  ComponentForms,
} from '@packages/systems/flows/types';
import {
  Component,
  ExpressionPath,
  Expressions,
  matchExpression,
  Types,
  Plugins,
} from '@packages/systems/wfdl';
import {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';

const sourcePath = {in: 'Record', at: 'Source'} as const;
const formPath = {in: 'Record', at: 'form'} as const;
const attrPath = {in: 'Record', at: 'attr'} as const;
const visibilityPath = {in: 'Record', at: 'visibility'} as const;

const flowIdPath = [
  sourcePath,
  {in: 'Union', tag: 'Logic'},
  {in: 'Record', at: 'flowId'},
];
const NAME_PATH = [formPath, {in: 'Record', at: 'name'}];
const SELECT_OPTIONS_PATH = [formPath, {in: 'Record', at: 'opts'}];
const SELECT_OPTION_VALUE = [{in: 'Record', at: 'v'}];
const SELECT_MULTIPLE_PATH = [attrPath, {in: 'Record', at: 'multiple'}];
const REQUIRED_PATH = [
  attrPath,
  {in: 'Record', at: 'required'},
] satisfies ExpressionPath;

const INPUT_TYPE_PATH = [attrPath, {in: 'Record', at: 'type'}];
const RADIO_VALUE_PATH = [attrPath, {in: 'Record', at: 'value'}];
const FILE_MANAGE_TYPES_PATH = [formPath, {in: 'Record', at: 'manageTypes'}];
const FILE_ACCEPT_DOCUMENTS_PATH = [formPath, {in: 'Record', at: 'acceptDocuments'}]; // prettier-ignore
const FILE_ACCEPT_IMAGES_PATH = [formPath, {in: 'Record', at: 'acceptImages'}];
const FILE_ACCEPT_CUSTOM_PATH = [formPath, {in: 'Record', at: 'acceptCustom'}];
const FILE_CUSTOM_TYPES_PATH = [formPath, {in: 'Record', at: 'customTypes'}];

const REDIRECT_PATH = [attrPath, {in: 'Record', at: 'redirect'}];
const ACTION_PATH = [attrPath, {in: 'Record', at: 'action'}];
const METHOD_PATH = [attrPath, {in: 'Record', at: 'method'}];
const PASSWORD_PAGE_PATH = [formPath, {in: 'Record', at: 'passwordPage'}];

const PLACEHOLDER_PATH = [attrPath, {in: 'Record', at: 'placeholder'}];
const VISIBILITY_PATH = [
  visibilityPath,
  {in: 'Record', at: 'conditions'},
] satisfies ExpressionPath;

const getExprElementId = matchExpression({
  Element: Expressions.getElementId,
  _: (_) => undefined,
});

/**
 * Walks element tree recursively and returns a list of form wrapper elements
 */
function findAllFormForm(element: ExpressionElement) {
  return _findAllFormForm(element, []);
}

function _findAllFormForm(
  element: ExpressionElement,
  mutated_formsQueue: ExpressionElement[]
) {
  if (isFormForm(element)) {
    mutated_formsQueue.push(element);
    // return early because FormForm cannot nest inside each other
    return mutated_formsQueue;
  }

  const children = getDescendants(element) || emptyArray;

  children.forEach((childElement) => {
    _findAllFormForm(childElement, mutated_formsQueue);
  });

  return mutated_formsQueue;
}

export function collectFieldsInsideForm(
  formFormElement: ExpressionElement,
  additionalFields?: {
    password?: boolean;
    recaptcha?: boolean;
  }
): Form {
  const {fields, radioMap} = reduceElementTree(
    formFormElement,
    (acc, childElement) => {
      const childData = Expressions.getElementData(childElement);
      const name = getTextWithFallback(childData, NAME_PATH);
      const required = getBooleanWithFallback(childData, REQUIRED_PATH);
      const placeholder = getTextWithFallback(childData, PLACEHOLDER_PATH);
      const visibility = Expressions.getIn(childData, VISIBILITY_PATH);
      const includePassword = additionalFields?.password || false;
      const includeRecaptcha = additionalFields?.recaptcha || false;

      if (isFormCheckboxInput(childElement)) {
        // @ts-expect-error - TS2345 - Argument of type '{ fieldName: string; required: boolean; fieldType: { tag: string; val: Record<string, any>; }; visibility: any; }' is not assignable to parameter of type 'never'.
        acc.fields.push({
          fieldName: name,
          required,
          fieldType: {tag: 'Checkbox', val: emptyObject},
          visibility,
        });
      }

      if (isFormTextarea(childElement)) {
        // @ts-expect-error - TS2345 - Argument of type '{ fieldName: string; required: boolean; fieldType: { tag: string; val: Record<string, any>; }; placeholder: string; visibility: any; }' is not assignable to parameter of type 'never'.
        acc.fields.push({
          fieldName: name,
          required,
          fieldType: {tag: 'Plain', val: emptyObject},

          placeholder,
          visibility,
        });
      }

      if (isFormFileUpload(childElement)) {
        const manageTypes = getBooleanWithFallback(
          childData,
          // @ts-expect-error - TS2345 - Argument of type '({ readonly in: "Record"; readonly at: "form"; } | { in: string; at: string; })[]' is not assignable to parameter of type '{ at: string; in: "Record"; }[]'.
          FILE_MANAGE_TYPES_PATH
        );
        const acceptDocuments = getBooleanWithFallback(
          childData,
          // @ts-expect-error - TS2345 - Argument of type '({ readonly in: "Record"; readonly at: "form"; } | { in: string; at: string; })[]' is not assignable to parameter of type '{ at: string; in: "Record"; }[]'.
          FILE_ACCEPT_DOCUMENTS_PATH
        );
        const acceptImages = getBooleanWithFallback(
          childData,
          // @ts-expect-error - TS2345 - Argument of type '({ readonly in: "Record"; readonly at: "form"; } | { in: string; at: string; })[]' is not assignable to parameter of type '{ at: string; in: "Record"; }[]'.
          FILE_ACCEPT_IMAGES_PATH
        );
        const acceptCustom = getBooleanWithFallback(
          childData,
          // @ts-expect-error - TS2345 - Argument of type '({ readonly in: "Record"; readonly at: "form"; } | { in: string; at: string; })[]' is not assignable to parameter of type '{ at: string; in: "Record"; }[]'.
          FILE_ACCEPT_CUSTOM_PATH
        );
        const customTypes = getTextWithFallback(
          childData,
          FILE_CUSTOM_TYPES_PATH
        );

        // @ts-expect-error - TS2345 - Argument of type '{ fieldName: string; required: boolean; fieldType: { tag: string; val: { manageTypes: boolean; acceptDocuments: boolean; acceptImages: boolean; acceptCustom: boolean; customTypes: string; }; }; }' is not assignable to parameter of type 'never'.
        acc.fields.push({
          fieldName: name,
          required,
          fieldType: {
            tag: 'File',
            val: {
              manageTypes,
              acceptDocuments,
              acceptImages,
              acceptCustom,
              customTypes,
            },
          },
        });
      }

      if (isFormBasicInputElement(childElement)) {
        // @ts-expect-error - TS2345 - Argument of type '({ readonly in: "Record"; readonly at: "attr"; } | { in: string; at: string; })[]' is not assignable to parameter of type '{ at: string; in: "Record"; }[]'.
        const inputType = getEnumWithFallback(childData, INPUT_TYPE_PATH);

        // default inputType === 'text';
        let fieldType = {tag: 'Plain', val: {}};
        if (inputType === 'email') fieldType = {tag: 'Email', val: {}};
        if (inputType === 'tel') fieldType = {tag: 'Phone', val: {}};
        if (inputType === 'number') fieldType = {tag: 'Number', val: {}};
        //
        // Note: In general, password fields are only available on USYS pages
        // (login, signup, update, reset) and password protected pages. Forms
        // aren't actually allowed to be submitted if there is a password
        // field anywhere else, so we exclude being able to bind to a password
        // field.
        //
        if (inputType === 'password') {
          // @ts-expect-error - TS2322 - Type 'null' is not assignable to type '{ tag: string; val: {}; }'.
          fieldType = includePassword ? {tag: 'Password', val: {}} : null;
        }

        if (fieldType) {
          // @ts-expect-error - TS2345 - Argument of type '{ fieldName: string; required: boolean; fieldType: { tag: string; val: {}; }; placeholder: string; visibility: any; }' is not assignable to parameter of type 'never'.
          acc.fields.push({
            fieldName: name,
            required,
            fieldType,
            placeholder,
            visibility,
          });
        }
      }

      if (isFormSelect(childElement)) {
        const allowMultiple = getBooleanWithFallback(
          childData,
          // @ts-expect-error - TS2345 - Argument of type '({ readonly in: "Record"; readonly at: "attr"; } | { in: string; at: string; })[]' is not assignable to parameter of type '{ at: string; in: "Record"; }[]'.
          SELECT_MULTIPLE_PATH
        );
        const optionsListExpr = getListWithFallback(
          childData,
          // @ts-expect-error - TS2345 - Argument of type '({ readonly in: "Record"; readonly at: "form"; } | { in: string; at: string; })[]' is not assignable to parameter of type '{ at: string; in: "Record"; }[]'.
          SELECT_OPTIONS_PATH
        );
        const choices = optionsListExpr.map<string>(
          (choiceRecordExpr: Expression) =>
            getTextWithFallback(choiceRecordExpr, SELECT_OPTION_VALUE)
        );

        // @ts-expect-error - TS2345 - Argument of type '{ fieldName: string; required: boolean; fieldType: { tag: string; val: { choices: string[]; allowMultiple: boolean; }; }; visibility: any; }' is not assignable to parameter of type 'never'.
        acc.fields.push({
          fieldName: name,
          required,
          fieldType: {
            tag: 'Select',
            val: {
              choices,
              allowMultiple,
            },
          },
          visibility,
        });
      }

      if (includeRecaptcha && isFormReCaptcha(childElement)) {
        // @ts-expect-error - TS2345 - Argument of type '{ fieldName: string; required: boolean; fieldType: { tag: string; val: { choices: string[]; allowMultiple: boolean; }; }; visibility: any; }' is not assignable to parameter of type 'never'.
        acc.fields.push({
          fieldName: 'ReCAPTCHA',
          required,
          fieldType: {
            tag: 'ReCaptcha',
            val: {},
          },
          visibility,
        });
      }

      if (isFormRadioInput(childElement)) {
        // Batch radio inputs by group name to process them as one field later
        const choice = getTextWithFallback(childData, RADIO_VALUE_PATH);
        let choices = acc.radioMap.get(name);

        if (choices === undefined) {
          choices = {
            // Index to maintain field order for radio inputs
            fieldIndex: acc.fields.length,
            values: [],
          };

          // Push an empty Radio field type to maintain field order
          // @ts-expect-error - TS2345 - Argument of type '{ fieldName: string; required: boolean; fieldType: { tag: string; val: { choices: never[]; }; }; visibility: any; }' is not assignable to parameter of type 'never'.
          acc.fields.push({
            fieldName: name,
            required,
            fieldType: {
              tag: 'Radio',
              val: {choices: []},
            },
            visibility,
          });
        }

        choices.values.push({
          choice,
          required,
        });
        acc.radioMap.set(name, choices);
      }

      return acc;
    },
    {
      fields: [],
      radioMap: new Map(),
    }
  );

  const formName = getTextWithFallback(
    Expressions.getElementData(formFormElement),
    NAME_PATH
  );
  const elementId = Expressions.getElementId(formFormElement);
  const flowId = getTextWithFallback(
    Expressions.getElementData(formFormElement),
    flowIdPath
  );
  const formData = Expressions.getElementData(formFormElement);
  const passwordPage = getBooleanWithFallback(
    formData,
    // @ts-expect-error - TS2345 - Argument of type '({ readonly in: "Record"; readonly at: "form"; } | { in: string; at: string; })[]' is not assignable to parameter of type '{ at: string; in: "Record"; }[]'.
    PASSWORD_PAGE_PATH
  );
  const responseSettings = {
    redirectURL: getTextWithFallback(formData, REDIRECT_PATH),
    action: getTextWithFallback(formData, ACTION_PATH),
    // @ts-expect-error - TS2345 - Argument of type '({ readonly in: "Record"; readonly at: "attr"; } | { in: string; at: string; })[]' is not assignable to parameter of type '{ at: string; in: "Record"; }[]'.
    method: getEnumWithFallback(formData, METHOD_PATH),
  } as const;

  for (const [groupName, {fieldIndex, values}] of radioMap) {
    // @ts-expect-error - TS2339 - Property 'fieldType' does not exist on type 'never'.
    if (fields[fieldIndex] && fields[fieldIndex].fieldType.tag === 'Radio') {
      // @ts-expect-error - TS2322 - Type '{ fieldName: any; required: any; fieldType: { tag: string; val: { choices: any; }; }; }' is not assignable to type 'never'.
      fields[fieldIndex] = {
        fieldName: groupName,
        // @ts-expect-error - TS7006 - Parameter 'choice' implicitly has an 'any' type.
        required: values.some((choice) => choice.required),
        fieldType: {
          tag: 'Radio',
          // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments. | TS7006 - Parameter 'choice' implicitly has an 'any' type.
          val: {choices: values.map<string>((choice) => choice.choice)},
        },
      };
    }
  }

  return {
    name: formName,
    elementId,
    fields,
    ...(flowId ? {flowId} : null),
    ...(passwordPage ? {passwordPage} : null),

    // Store additional metadata about the form
    responseSettings,
  };
}

/**
 * Walks a single slot content element looking for forms. Slot content can be
 * a bare form, a component instance, or a plain wrapper element with either
 * of those buried arbitrarily deep inside it, so this descends through plain
 * elements and only stops at a form or a component instance boundary.
 *
 * `enclosingSymbolId` is the symbolId of whichever symbol's own authored
 * render literally contains this slot fill (null if that's just the page).
 * Slot content is physically embedded in the caller's render/data, so a bare
 * form (or one under a plain wrapper) belongs to that enclosing symbol, same
 * as the Designer's own componentId lookup for form settings - it's only a
 * nested component instance's *own* definition that gets a different,
 * independent symbolId.
 */
const collectFormsFromSlotElement = (
  componentMap: Component.ComponentMap,
  element: ExpressionElement,
  enclosingSymbolId: string | null
): Array<Form & {symbolId: string | null}> => {
  if (isFormForm(element)) {
    // Return early because FormForm cannot nest inside each other
    return [{...collectFieldsInsideForm(element), symbolId: enclosingSymbolId}];
  }

  if (isUserComponentInst(componentMap, element)) {
    const nestedUserComponent = componentMap.get(element.type);
    if (!nestedUserComponent) {
      return emptyArray;
    }

    // Collect forms from the nested component's render tree
    const nestedComponentForms =
      collectFormsFromComponent(componentMap)(nestedUserComponent);

    // Recursively collect forms from the nested component instance's slot
    // content - still physically part of the same enclosing symbol's render
    const nestedSlotForms = collectFormsFromSlotContent(
      componentMap,
      element,
      enclosingSymbolId
    );

    return [...nestedComponentForms, ...nestedSlotForms];
  }

  // Plain wrapper element - descend into its children looking for forms or
  // components nested further down
  const children = getDescendants(element) || emptyArray;
  return children.flatMap((child) =>
    collectFormsFromSlotElement(componentMap, child, enclosingSymbolId)
  );
};

/**
 * Helper function to collect forms from slot content of a component instance.
 * This is extracted to enable recursive processing of nested slots.
 */
const collectFormsFromSlotContent = (
  componentMap: Component.ComponentMap,
  componentInstance: ExpressionElement,
  enclosingSymbolId: string | null
): Array<Form & {symbolId: string | null}> => {
  const userComponent = componentMap.get(componentInstance.type);
  if (!userComponent) {
    return emptyArray;
  }

  const componentDataType = Component.getDataType(userComponent);
  const componentProperties = Types.getRecordFields(componentDataType);
  const slotForms: Array<Form & {symbolId: string | null}> = [];

  if (componentProperties) {
    const elementData = Expressions.getElementData(componentInstance);

    for (const [propertyId, propertyType] of Object.entries(
      componentProperties
    )) {
      // Check if this property is a SlotContent type
      if (Types.isSlotContentType(propertyType)) {
        // Get the slot content from the component instance data
        const slotContentExpr = Expressions.getIn(elementData, [
          {in: 'Record', at: propertyId},
        ]);
        // Get the list of elements in the slot content
        const slotElements = slotContentExpr
          ? Expressions.getList(slotContentExpr) || []
          : [];

        // For each element in the slot, collect forms recursively
        for (const slotElementExpr of slotElements) {
          const slotElement = Expressions.getElement(slotElementExpr);
          if (slotElement) {
            slotForms.push(
              ...collectFormsFromSlotElement(
                componentMap,
                slotElement,
                enclosingSymbolId
              )
            );
          }
        }
      }
    }
  }

  return slotForms;
};

export const collectFormsFromComponent: (
  componentMap: Component.ComponentMap
) => (component: Component.ComponentType) => Array<
  Form & {
    symbolId: string | null;
  }
> = weakMemo((componentMap: Component.ComponentMap) =>
  weakMemo(
    (
      component: Component.ComponentType
    ): Array<
      Form & {
        symbolId: string | null;
      }
    > => {
      const render = Component.getRender(component);
      if (!render) {
        return [];
      }
      const rootElementId = getExprElementId(render);
      const symbolId =
        rootElementId && isUserComponentRootId(componentMap, rootElementId)
          ? rootElementId
          : null;
      return Expressions.everything(concatTo)(
        matchExpression({
          Element: (element) => {
            if (isFormForm(element)) {
              const formData = collectFieldsInsideForm(element);
              return Expressions.stop([{...formData, symbolId}]);
            }
            if (isUserComponentInst(componentMap, element)) {
              const userComponent = componentMap.get(element.type);
              if (!userComponent) {
                return Expressions.resume(emptyArray);
              }

              // Collect forms from the component itself
              const componentForms =
                collectFormsFromComponent(componentMap)(userComponent);

              // Collect forms from slot content passed to this component
              // instance - it's physically part of this render, so it
              // belongs to this render's own symbolId
              const slotForms = collectFormsFromSlotContent(
                componentMap,
                element,
                symbolId
              );

              return Expressions.stop([...componentForms, ...slotForms]);
            }
            return Expressions.resume(emptyArray);
          },
          _: constant(Expressions.resume(emptyArray)),
        })
      )(render);
    }
  )
);

/**
 * Walks element tree recursively and returns all the form field types
 */
export const collectFormFields: (expr: Expression) => Forms = weakMemo(
  function collectFormFields(expr: Expression): Forms {
    const element = Expressions.getElement(expr);
    if (element == null) return emptyArray;

    const formForms = findAllFormForm(element);

    return formForms.map((formFormElement) => {
      return collectFieldsInsideForm(formFormElement);
    });
  }
);

function getBooleanWithFallback(
  data: Expression,
  path: Array<{
    at: string;
    in: 'Record';
  }>
) {
  const expr = Expressions.getIn(data, path);
  return expr ? Expressions.getBoolean(expr) || false : false;
}
// @ts-expect-error - TS7006 - Parameter 'path' implicitly has an 'any' type.
function getTextWithFallback(data: Expression, path): string {
  const expr = Expressions.getIn(data, path);
  return expr ? Expressions.getText(expr) || '' : '';
}
function getEnumWithFallback(
  data: Expression,
  path: Array<{
    at: string;
    in: 'Record';
  }>
) {
  const expr = Expressions.getIn(data, path);
  return expr ? Expressions.getEnum(expr) || '' : '';
}
function getListWithFallback(
  data: Expression,
  path: Array<{
    at: string;
    in: 'Record';
  }>
): Array<Expression> {
  const expr = Expressions.getIn(data, path);
  return expr ? Expressions.getList(expr) || [] : [];
}

const pageName = Component.name('page');

const _collectFormFieldsFromSitePlugin = singleMemo((pageId: string) =>
  singleMemo((pageTitle: string) =>
    weakMemo((componentMap: Component.ComponentMap): ComponentForms => {
      return componentMap.reduceAtPluginName(
        Plugins.name('__SitePlugin'),
        (result, component, componentName) => {
          const root = Component.getRender(component);

          const isPage = pageName === componentName;
          const name = isPage ? pageId : componentName;
          const componentId = name as ComponentId;

          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
          result[componentId] = {
            forms: collectFormFields(root),
            title: isPage ? pageTitle : Component.getDisplayName(component),
          };

          return result;
        },
        {}
      );
    })
  )
);

export function collectFormFieldsFromSitePlugin(
  /** Current component map in the Designer */
  componentMap: Component.ComponentMap,
  /** Page id where `collectFormFieldsFromSitePlugin` is called from. */
  pageId: ComponentId,
  /** User-defined page title (e.g. Home, Contact, About) */
  pageTitle: string
): ComponentForms {
  return _collectFormFieldsFromSitePlugin(pageId)(pageTitle)(componentMap);
}
